<template>
	<div :class="[wrapperClasses, 'vue-suggestion']">
		<div :class="[{ vs__selected: value }, inputWrapperClasses, 'vs__input-group']">
			<input
				v-model="searchText"
				type="search"
				:class="[inputClasses, 'vs__input']"
				:placeholder="placeholder"
				:disabled="disabled"
				:maxlength="maxLen"
				@blur="blur"
				@focus="focus"
				@input="inputChange"
				@keydown.enter.prevent="keyEnter"
				@keydown.up.prevent="keyUp"
				@keydown.down.prevent="keyDown"
			/>
			<slot name="searchSlot" />
		</div>
		<slot v-if="loading" name="loading">
			<div class="vs__loading">Loading...</div>
		</slot>
		<slot v-else-if="showList" name="suggestionList">
			<div :class="[suggestionListClasses, 'vs__list']">
				<div v-for="(group, index) in itemGroups" :class="suggestionGroupClasses" :key="index">
					<div v-if="itemGroups.length > 1 || group.header" :class="[suggestionGroupHeaderClasses, 'vs__group-header']">
						{{ group.header }}
					</div>
					<div
						v-for="item in group.items"
						:key="item.vsItemIndex"
						:class="[{ 'vs__item-active': item.vsItemIndex === cursor }, suggestionItemWrapperClasses, 'vs__list-item']"
						@click="selectItem(item)"
						@mouseover="cursor = item.vsItemIndex"
					>
						<div>
							<slot name="content" :item="item">
								<span>{{ item.data.label }}</span>
							</slot>
						</div>
					</div>
				</div>
			</div>
		</slot>
	</div>
</template>

<script>
let utils = {
	minLen: 2,
	maxLen: 100,
	value: "",
	setlabel: (item) => item.data.label,
	items: () => [],
	placeholder: "",
	inputClasses: "",
	wrapperClasses: "",
	inputWrapperClasses: "",
	suggestionListClasses: "",
	suggestionGroupClasses: "",
	suggestionGroupHeaderClasses: "",
	suggestionItemWrapperClasses: "",
	suggestionItemClasses: "",
};
export default {
	name: "autocomplete",
	props: {
		itemTemplate: {
			type: Object,
			default: utils.itemTemplate,
		},
		minLen: {
			type: Number,
			default: utils.minLen,
		},
		maxLen: {
			type: Number,
			default: utils.maxLen,
		},
		value: {
			type: [Object, String, Number],
			default: utils.value,
		},
		setlabel: {
			type: Function,
			default: utils.setlabel,
		},
		items: {
			type: Array,
			default: utils.items,
		},
		disabled: {
			type: Boolean,
			default: utils.disabled,
		},
		loading: {
			type: Boolean,
			default: false,
		},
		placeholder: {
			type: String,
			default: utils.placeholder,
		},
		inputClasses: {
			type: String,
			default: utils.inputClasses,
		},
		wrapperClasses: {
			type: String,
			default: utils.wrapperClasses,
		},
		inputWrapperClasses: {
			type: String,
			default: utils.inputWrapperClasses,
		},
		suggestionListClasses: {
			type: String,
			default: utils.suggestionListClasses,
		},
		suggestionGroupClasses: {
			type: String,
			default: utils.suggestionGroupClasses,
		},
		suggestionGroupHeaderClasses: {
			type: String,
			default: utils.suggestionGroupHeaderClasses,
		},
		suggestionItemWrapperClasses: {
			type: String,
			default: utils.suggestionItemWrapperClasses,
		},
		suggestionItemClasses: {
			type: String,
			default: utils.suggestionItemClasses,
		},
	},
	data() {
		return {
			searchText: "",
			showList: false,
			cursor: 0,
		};
	},
	computed: {
		itemGroups() {
			return this.items.reduce((prv, crr, index) => {
				const groupName = crr.suggestionGroup || "";
				const item = {
					...crr,
					vsItemIndex: index,
				};
				const foundGroup = prv.find((gr) => gr.header === groupName);
				if (foundGroup) {
					foundGroup.items.push(item);
				} else {
					prv.push({
						header: groupName,
						items: [item],
					});
				}
				return prv;
			}, []);
		},
	},
	watch: {
		value(newVal) {
			if (newVal != undefined) {
				this.searchText = newVal;
			}
		},
		// eslint-disable-next-line func-names
		"items.length": function () {
			this.showList = this.isAbleToShowList();
		},
	},
	created() {
		// this.checkMissingProps();
	},
	mounted() {
		if (this.value != undefined) {
			this.searchText = this.value.data == undefined ? "" : this.setlabel(this.value);
		}
	},
	methods: {
		inputChange() {
			this.showList = this.isAbleToShowList();
			this.cursor = 0;
			this.$emit("changed", this.searchText);
		},
		isAbleToShowList() {
			return (this.searchText || "").length >= this.minLen && this.items.length > 0;
		},
		checkMissingProps() {
			if (!this.itemTemplate) {
				console.warn("You need to pass `template` as the suggestion list item template");
			}
		},
		focus() {
			this.$emit("focus", this.searchText);
			this.showList = this.isAbleToShowList();
		},
		blur() {
			this.$emit("blur", this.searchText);
			// set timeout for the click event to work
			setTimeout(() => {
				this.showList = false;
			}, 200);
		},
		selectItem({ ...item } = {}) {
			// was { vsItemIndex ...item }
			if (item) {
				this.searchText = this.setlabel(item);
				this.$emit("selected", item.data.label);
			}
			this.$emit("input", item.data.label);
		},
		keyUp() {
			this.$emit("key-up", this.searchText);
			if (this.cursor > 0) {
				this.cursor -= 1;
			}
		},
		keyDown() {
			this.$emit("key-down", this.searchText);
			if (this.cursor < this.items.length - 1) {
				this.cursor += 1;
			}
		},
		keyEnter() {
			if (this.showList && this.items[this.cursor]) {
				this.selectItem(this.items[this.cursor]);
				this.showList = false;
			}
			this.$emit("enter", this.items[this.cursor].data.label);
		},
	},
};
</script>

<style>
.vue-suggestion {
	width: 160px;
}

.vue-suggestion .vs__list .vs__list-item {
	cursor: pointer;
}

.vue-suggestion .vs__list .vs__list-item.vs__item-active {
	background-color: #fff;
}

.vs__list-item {
	border-bottom: 1px solid black;
}

.vs__list-item span {
	margin-left: 10px;
	margin-right: 10px;
	margin-top: 5px;
	margin-bottom: 5px;
}

.vs__list {
	max-width: 160px;
	position: fixed;
	background-color: #fff;
	box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
	z-index: 5;
}
</style>
